<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, provide, ref } from "vue";
import { storeToRefs } from "pinia";

import { searchContextSymbol } from "@/constants";

import ContentSearch from "@/components/content/ContentSearch.vue";
import { useAppBreadcrumbs } from "@/composables/useAppBreadcrumbs";

import type { WebinarFilters } from "@/composables/filters/webinar-filters";
import { webinarFiltersSymbol } from "@/plugins/ContentFiltersPlugin";

import type { ProductFilter } from "@/composables/filters/product-filter";
import type { CategoryFilter } from "@/composables/filters/category-filter";
import type { PagingFilter } from "@/composables/filters/paging-filter";
import { categoryFilterSymbol, pagingFilterSymbol, productFilterSymbol } from "@/plugins/ContentFiltersPlugin";
import { useApplicationStore } from "@/stores/Application";
import { useWebinarsStore } from "@/stores/Webinars";
import { useDateRangeFilter } from "@/composables/filters/date-range-filter";

const {
  filteredWebinarsPage,
  filteredWebinars,
  hasAnyWebinarResults,
  webinarsSearchResultsLabel
} = inject< WebinarFilters >( webinarFiltersSymbol ) as WebinarFilters;

const webinarsStore = useWebinarsStore();
const { webinarBySlug } = webinarsStore;
const { webinarCategories, isLoading } = storeToRefs( webinarsStore );

const { setSelectedWebinar } = useApplicationStore();

const { trail, labelService } = useAppBreadcrumbs();
const breadcrumbItems = computed( () => trail( { label: labelService.label( 'lblWebinars' ) } ) );

provide( searchContextSymbol, {
  breadcrumbItems,
  contentBySlug: webinarBySlug,
  contentCategories: webinarCategories,
  contentTitle: "Webinars",
  contentType: 'webinar',
  filteredContent: filteredWebinars,
  filteredContentPage: filteredWebinarsPage,
  hasAnyResults: hasAnyWebinarResults,
  isLoading: isLoading,
  hideCategoryFilter: false,
  hideProductFilter: false,
  searchPlaceholder: "Search Webinars",
  searchResultsLabel: webinarsSearchResultsLabel,
  setSelectedContent: setSelectedWebinar
} );

const productFilter = inject< ProductFilter >( productFilterSymbol ) as ProductFilter;
const categoryFilter = inject< CategoryFilter >( categoryFilterSymbol ) as CategoryFilter;
const pagingFilter = inject< PagingFilter >( pagingFilterSymbol ) as PagingFilter;
const { clearDateRangeFilter, datesFilter } = useDateRangeFilter();

const show = ref< boolean >( false );
onMounted( async () => {
  // Clear all product filters set
  productFilter?.resetSelectedProducts();

  // Clear all category filters set
  categoryFilter?.resetSelectedCategories();

  // Reset the page # of the results back to 0
  pagingFilter?.resetPageNumber();

  const now = new Date();
  const oneMonthAhead = new Date( now );
  oneMonthAhead.setMonth( oneMonthAhead.getMonth() + 1 );
  datesFilter.value = [ datesFilter.value[ 0 ], oneMonthAhead ];

  webinarsStore.initialize();

  show.value = true;
} );

onUnmounted( () => {
  show.value = false;
  clearDateRangeFilter();
} );

webinarsStore.initialize();
</script>

<template>
  <transition mode="out-in" name="slide-down">
    <ContentSearch v-if="show"/>
  </transition>
</template>
